/**
 * CiaoConfirmation — Batched write operation confirmation widget
 * Shows one or more action summaries with accept/cancel buttons and optional JSON detail
 */

import React, { useState } from 'react';
import { IoCheckmarkCircle, IoCloseCircle, IoWarning, IoShield, IoCodeSlash, IoChevronDown, IoChevronUp } from 'react-icons/io5';
import type { PendingConfirmation } from '../modules/ciaoApi';
import ReactMarkdown from 'react-markdown';

interface CiaoConfirmationProps {
  confirmation: PendingConfirmation;
  onAccept: () => void;
  onCancel: () => void;
  isProcessing: boolean;
}

const RISK_CONFIG = {
  low: { color: '#22c55e', bg: 'rgba(34,197,94,0.08)', label: 'Bajo riesgo', icon: IoShield },
  medium: { color: '#f59e0b', bg: 'rgba(245,158,11,0.08)', label: 'Riesgo medio', icon: IoWarning },
  high: { color: '#ef4444', bg: 'rgba(239,68,68,0.08)', label: 'Alto riesgo', icon: IoWarning },
};

const CiaoConfirmation: React.FC<CiaoConfirmationProps> = ({
  confirmation,
  onAccept,
  onCancel,
  isProcessing,
}) => {
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
  const riskCfg = RISK_CONFIG[confirmation.overallRisk];
  const RiskIcon = riskCfg.icon;
  const isBatch = confirmation.items.length > 1;

  const toggleItem = (idx: number) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  return (
    <div
      className="ciao-animate-fade-in"
      style={{
        margin: '8px 0',
        borderRadius: 12,
        overflow: 'hidden',
        border: `1px solid ${riskCfg.color}20`,
        background: 'var(--card-color, #f8f8f8)',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '10px 14px',
          background: riskCfg.bg,
          borderBottom: `1px solid ${riskCfg.color}15`,
        }}
      >
        <RiskIcon size={16} color={riskCfg.color} />
        <span style={{ fontSize: 12, fontWeight: 600, color: riskCfg.color }}>
          Confirmación requerida
        </span>
        {isBatch && (
          <span
            style={{
              fontSize: 10,
              padding: '2px 8px',
              borderRadius: 10,
              background: `${riskCfg.color}18`,
              color: riskCfg.color,
              fontWeight: 600,
            }}
          >
            {confirmation.items.length} acciones
          </span>
        )}
        <span
          style={{
            marginLeft: 'auto',
            fontSize: 10,
            padding: '2px 8px',
            borderRadius: 10,
            background: `${riskCfg.color}18`,
            color: riskCfg.color,
            fontWeight: 500,
          }}
        >
          {riskCfg.label}
        </span>
      </div>

      {/* Action items */}
      {confirmation.items.map((item, idx) => {
        const itemRiskCfg = RISK_CONFIG[item.risk];
        const isExpanded = expandedItems.has(idx);
        return (
          <div
            key={idx}
            style={{
              padding: '10px 14px',
              borderBottom: idx < confirmation.items.length - 1 ? '1px solid rgba(0,0,0,0.05)' : undefined,
            }}
          >
            {/* Item header with step number for batches */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              {isBatch && (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    background: itemRiskCfg.bg,
                    color: itemRiskCfg.color,
                    fontSize: 10,
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {idx + 1}
                </span>
              )}
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-color, #333)' }}>
                {item.actionDescription}
              </span>
            </div>

            {/* Summary */}
            <div
              className="ciao-markdown"
              style={{ fontSize: 12, lineHeight: 1.6, color: 'var(--text-color, #333)', paddingLeft: isBatch ? 28 : 0 }}
            >
              <ReactMarkdown
                components={{ strong: ({ children }) => <>{children}</> }}
              >
                {item.summary}
              </ReactMarkdown>
            </div>

            {/* JSON toggle */}
            {item.args && (
              <div style={{ paddingLeft: isBatch ? 28 : 0, marginTop: 4 }}>
                <button
                  onClick={() => toggleItem(idx)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '3px 10px',
                    borderRadius: 6,
                    background: isExpanded ? 'rgba(99,102,241,0.12)' : 'rgba(0,0,0,0.04)',
                    color: isExpanded ? '#6366f1' : 'var(--text-muted, #999)',
                    fontSize: 10,
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    border: 'none',
                  }}
                >
                  <IoCodeSlash size={11} />
                  {isExpanded ? 'Ocultar JSON' : 'Ver JSON'}
                  {isExpanded ? <IoChevronUp size={10} /> : <IoChevronDown size={10} />}
                </button>
                {isExpanded && (
                  <pre
                    style={{
                      marginTop: 8,
                      padding: 10,
                      borderRadius: 8,
                      background: 'rgba(0,0,0,0.04)',
                      fontSize: 10,
                      lineHeight: 1.5,
                      color: 'var(--text-muted, #666)',
                      overflow: 'auto',
                      maxHeight: 200,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {JSON.stringify(item.args, null, 2)}
                  </pre>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Actions */}
      <div
        style={{
          display: 'flex',
          gap: 8,
          padding: '8px 14px 12px',
        }}
      >
        <button
          onClick={onAccept}
          disabled={isProcessing}
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            padding: '8px 14px',
            borderRadius: 8,
            background: '#22c55e',
            color: '#fff',
            fontSize: 12,
            fontWeight: 600,
            cursor: isProcessing ? 'not-allowed' : 'pointer',
            opacity: isProcessing ? 0.6 : 1,
            transition: 'opacity 0.15s, background 0.15s',
            border: 'none',
          }}
          onMouseEnter={(e) => { if (!isProcessing) e.currentTarget.style.background = '#16a34a'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = '#22c55e'; }}
        >
          <IoCheckmarkCircle size={14} />
          {isProcessing ? 'Ejecutando...' : isBatch ? `Aceptar todo (${confirmation.items.length})` : 'Aceptar'}
        </button>
        <button
          onClick={onCancel}
          disabled={isProcessing}
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            padding: '8px 14px',
            borderRadius: 8,
            background: 'rgba(0,0,0,0.06)',
            color: 'var(--text-color, #666)',
            fontSize: 12,
            fontWeight: 600,
            cursor: isProcessing ? 'not-allowed' : 'pointer',
            opacity: isProcessing ? 0.6 : 1,
            transition: 'opacity 0.15s, background 0.15s',
            border: 'none',
          }}
          onMouseEnter={(e) => { if (!isProcessing) e.currentTarget.style.background = 'rgba(0,0,0,0.1)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(0,0,0,0.06)'; }}
        >
          <IoCloseCircle size={14} />
          Cancelar
        </button>
      </div>
    </div>
  );
};

export default CiaoConfirmation;
